import React, { useEffect, useReducer, useImperativeHandle, useState, useRef } from 'react';
import { Col, Tooltip, Select, Row } from 'antd';
import PropTypes from 'prop-types';

import InfoIcon from 'components/Icons/InfoIcon';
import Input from 'components/Input';
import { userVar, fromAppVar } from 'graphql/cache';
import {
  ClientName,
  WixClientName,
  MondayClientName,
  HubspotClientName,
  PipedriveClientName,
  PipedriveDealName,
} from '../../../modules';
import { sanitizeString } from 'utils/xss';
import DropdownIcon from 'components/Icons/DropdownIcon';

const initialState = {
  name: '',
  contact: '',
  desc: '',
  email: '',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'setName':
      return { ...state, name: action.name };
    case 'setContact':
      return { ...state, contact: action.contact };
    case 'setDesc':
      return { ...state, desc: action.desc };
    case 'setEmail':
      return { ...state, email: action.email };
    case 'setMondayItemId':
      return { ...state, mondayItemId: action.id };
    case 'setHubspotContactId':
      return { ...state, hubspotContactId: action.hubspotContactId };
    case 'setDeal':
      return { ...state, deal: action.deal };
    default:
      throw new Error('Unexpected action');
  }
};

const ClientInformation = React.forwardRef(
  ({ prop, save, configText, fetchWixProductsAndServicesAndInfo, setWixSiteProperties }, ref) => {
    const userVariables = userVar();
    const [state, dispatch] = useReducer(reducer, initialState);
    const [selectedIntegration, setSelectedIntegration] = useState('');
    const hasManuallySelectedIntegration = useRef(false);
    const isSwitchingIntegration = useRef(false);

    useEffect(() => {
      if (prop?.client) {
        dispatch({ type: 'setName', name: prop.client?.name });
        dispatch({ type: 'setDesc', desc: prop.client?.desc });
        dispatch({ type: 'setEmail', email: prop.client?.email });
        dispatch({ type: 'setContact', contact: prop.client?.contact });
        dispatch({ type: 'setDeal', deal: prop.pipedriveDealId });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prop?.client]);

    const onChange = ({ type, action, value }) => {
      value = sanitizeString(value);

      // clear the selected integration (but not when we're switching integrations)
      if (type === 'setContact' && (!value || !value.trim()) && !isSwitchingIntegration.current) {
        setSelectedIntegration('');
      }

      if (
        type !== 'setContact' ||
        type === 'setEmail' ||
        type === 'setName' ||
        type === 'setMondayItemId' ||
        type === 'setHubspotContactId' ||
        type === 'setDeal'
      ) {
        dispatch({ type, [action]: value });
      }

      if (type === 'setDeal') {
        updateProposal({ pipedriveDealId: value });
      } else {
        const client = prop.client || {};
        client[action] = value?.trim();
        save({ client }, false, false, () => {}, 0);
      }
    };

    const updateProposal = ({
      email,
      instanceId = '',
      currency,
      pipedriveDealId,
      hubspotContactId,
    }) => {
      const updateQuery = {};

      if (email) {
        updateQuery['signatureEmails'] = [...new Set([...prop.signatureEmails, email])];
      }

      if (currency) {
        updateQuery['currency'] = currency;
      }

      if (instanceId) {
        updateQuery['wixConnectedInstance'] = instanceId;
      }

      if (pipedriveDealId) {
        updateQuery['pipedriveDealId'] = pipedriveDealId;
      }

      if (hubspotContactId) {
        updateQuery['hubspotContactId'] = hubspotContactId;
      }

      save(
        updateQuery,
        true,
        false,
        () => {
          if (instanceId && instanceId !== prop.wixConnectedInstance) {
            fetchWixProductsAndServicesAndInfo({
              variables: {
                instanceId: instanceId,
              },
            });
          }
        },
        0
      );
    };

    useImperativeHandle(
      ref,
      () => ({
        resetChosen: () => {
          dispatch({ type: 'setName', name: '' });
          dispatch({ type: 'setContact', contact: '' });
          dispatch({ type: 'setDesc', desc: '' });
          dispatch({ type: 'setEmail', email: '' });
          dispatch({ type: 'setDeal', deal: '' });
          save({ client: { ...initialState } }, true, false, () => {}, 0);
        },
      }),
      [dispatch, save]
    );

    // Get available integrations
    const getAvailableIntegrations = () => {
      const integrations = [];

      if (userVariables?.pipedrive?.token?.access_token) {
        integrations.push({ value: 'pipedrive', label: 'Pipedrive' });
      }

      if (Object.keys(userVariables?.monday || {})?.length > 0) {
        integrations.push({ value: 'monday', label: 'Monday.com' });
      }

      if (userVariables.hubspot?.token?.expires_in) {
        integrations.push({ value: 'hubspot', label: 'Hubspot' });
      }

      if (
        Array.isArray(userVariables?.wix) &&
        userVariables?.wix?.length > 0 &&
        userVariables?.wix?.some((wix) => wix?.instanceId)
      ) {
        integrations.push({ value: 'wix', label: 'Wix' });
      }

      return integrations;
    };

    const availableIntegrations = getAvailableIntegrations();

    // Initialize selectedIntegration from saved proposal data (only if user hasn't manually selected)
    useEffect(() => {
      if (!selectedIntegration && !hasManuallySelectedIntegration.current) {
        // Check if there's saved integration data in the proposal
        // Priority: Check which integration has active data that matches current contact
        const currentContact = prop?.client?.contact || state.contact;

        if (currentContact) {
          // If there's a contact, try to match it with integration data
          if (prop?.pipedriveDealId && (prop?.client?.contact || state.contact)) {
            setSelectedIntegration('pipedrive');
          } else if (
            (prop?.hubspot?.hubspotContactId || prop?.client?.hubspotContactId) &&
            (prop?.client?.contact || state.contact)
          ) {
            setSelectedIntegration('hubspot');
          } else if (prop?.wixConnectedInstance && (prop?.client?.contact || state.contact)) {
            setSelectedIntegration('wix');
          } else if (prop?.client?.mondayItemId && (prop?.client?.contact || state.contact)) {
            setSelectedIntegration('monday');
          }
        } else {
          // No contact yet, check integration data in order
          if (prop?.pipedriveDealId) {
            setSelectedIntegration('pipedrive');
          } else if (prop?.hubspot?.hubspotContactId || prop?.client?.hubspotContactId) {
            setSelectedIntegration('hubspot');
          } else if (prop?.wixConnectedInstance) {
            setSelectedIntegration('wix');
          } else if (prop?.client?.mondayItemId) {
            setSelectedIntegration('monday');
          } else if (fromAppVar() === 'hubspot') {
            setSelectedIntegration('hubspot');
          } else if (fromAppVar() === 'monday') {
            setSelectedIntegration('monday');
          } else if (availableIntegrations.length === 1) {
            setSelectedIntegration(availableIntegrations[0].value);
          }
        }
      }
    }, [
      availableIntegrations,
      selectedIntegration,
      prop?.pipedriveDealId,
      prop?.hubspot?.hubspotContactId,
      prop?.client?.hubspotContactId,
      prop?.wixConnectedInstance,
      prop?.client?.mondayItemId,
      prop?.client?.contact,
      state.contact,
    ]);

    return (
      <Col className="wizard-inner-content">
        {availableIntegrations.length >= 1 && (
          <div className="wizard-integration-selector">
            <Row>
              <label className="input-label">SELECT INTEGRATION (OPTIONAL)</label>
            </Row>
            <Select
              placeholder="Choose an integration"
              className="wizard-integration-selector-dropdown"
              value={selectedIntegration}
              onChange={(value) => {
                const previousIntegration = selectedIntegration;

                if (!value) {
                  // Clear integration and contact when integration is cleared
                  setSelectedIntegration('');
                  hasManuallySelectedIntegration.current = true;
                  onChange({ type: 'setContact', action: 'contact', value: '' });
                } else if (previousIntegration && previousIntegration !== value) {
                  // When switching integrations, set flag to prevent integration from being cleared
                  isSwitchingIntegration.current = true;

                  // Set new integration first
                  setSelectedIntegration(value);
                  hasManuallySelectedIntegration.current = true;

                  // Clear old integration data from proposal
                  if (previousIntegration === 'pipedrive') {
                    save({ pipedriveDealId: null }, false, false, () => {}, 0);
                    onChange({ type: 'setDeal', action: 'deal', value: '' });
                  } else if (previousIntegration === 'hubspot') {
                    const hubspotData = prop.hubspot || {};
                    hubspotData.hubspotContactId = null;
                    save({ hubspot: hubspotData }, false, false, () => {}, 0);
                    const client = prop.client || {};
                    client.hubspotContactId = '';
                    save({ client }, false, false, () => {}, 0);
                  } else if (previousIntegration === 'wix') {
                    save({ wixConnectedInstance: null }, false, false, () => {}, 0);
                  } else if (previousIntegration === 'monday') {
                    const client = prop.client || {};
                    client.mondayItemId = '';
                    save({ client }, false, false, () => {}, 0);
                  }

                  // Clear contact when switching integrations
                  onChange({ type: 'setContact', action: 'contact', value: '' });

                  // Reset flag after a brief delay to allow state updates to complete
                  setTimeout(() => {
                    isSwitchingIntegration.current = false;
                  }, 0);
                } else {
                  // First time selection (no previous integration)
                  setSelectedIntegration(value);
                  hasManuallySelectedIntegration.current = true;
                }
              }}
              allowClear
              options={availableIntegrations}
              suffixIcon={<DropdownIcon />}
            />
          </div>
        )}

        <Row className="wizard-client-information-row">
          <Col span={12}>
            {selectedIntegration === 'monday' ? (
              <MondayClientName
                type="setContact"
                action="contact"
                label="PROSPECT NAME"
                clientNameValue={state.contact || prop?.client?.contact || ''}
                onChange={onChange}
                language={prop.language}
                currency={prop.currency}
              />
            ) : selectedIntegration === 'hubspot' ? (
              <HubspotClientName
                type="setContact"
                action="contact"
                label="PROSPECT NAME"
                clientNameValue={state.contact || prop?.client?.contact || ''}
                onChange={onChange}
                language={prop.language}
                currency={prop.currency}
              />
            ) : selectedIntegration === 'wix' ? (
              <WixClientName
                type="setContact"
                action="contact"
                label="PROSPECT NAME"
                clientNameValue={state.contact || prop?.client?.contact || ''}
                onChange={onChange}
                language={prop.language}
                currency={prop.currency}
                updateProposal={updateProposal}
                setWixSiteProperties={setWixSiteProperties}
              />
            ) : selectedIntegration === 'pipedrive' ? (
              <PipedriveClientName
                type="setContact"
                action="contact"
                label="PROSPECT NAME"
                clientNameValue={state.contact || prop?.client?.contact || ''}
                onChange={onChange}
                language={prop.language}
              />
            ) : (
              <ClientName
                type="setContact"
                action="contact"
                clientNameValue={state.contact || prop?.client?.contact || ''}
                onChange={onChange}
                label="PROSPECT NAME"
                placeholder={configText('contact person name', true)}
              />
            )}
          </Col>

          <Col span={12}>
            <Input
              label={
                <>
                  <span>PROSPECT'S EMAIL</span>
                  <Tooltip
                    overlayClassName="wizard-tooltip-email"
                    title="Enter your prospect's email to send the proposal, schedule reminders, and utilize integrations"
                    color="black"
                    key="white">
                    <InfoIcon />
                  </Tooltip>
                </>
              }
              labelclass="wizard-email-label"
              placeholder={configText('wizard company email', true)}
              type="email"
              value={state.email}
              onChange={(e) =>
                onChange({
                  type: 'setEmail',
                  action: 'email',
                  value: e.target.value,
                })
              }
            />
          </Col>
        </Row>

        {selectedIntegration === 'pipedrive' && (
          <PipedriveDealName
            type="setDeal"
            action="deal"
            label="DEAL NAME"
            dealNameValue={state.deal || prop?.pipedriveDealId || 0}
            onChange={onChange}
            language={prop.language}
          />
        )}

        <Input
          label="COMPANY NAME"
          placeholder={configText('the company', true)}
          value={state.name}
          onChange={(e) =>
            onChange({
              type: 'setName',
              action: 'name',
              value: e.target.value,
            })
          }
        />

        <Input
          type="textarea"
          label="COMPANY DESCRIPTION"
          placeholder={configText('wizard company desc placeholder', true)}
          value={state.desc}
          onChange={(e) => onChange({ type: 'setDesc', action: 'desc', value: e.target.value })}
        />
      </Col>
    );
  }
);

ClientInformation.defaultProps = {};

ClientInformation.propTypes = {
  prop: PropTypes.instanceOf(Object).isRequired,
  configText: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  fetchWixProductsAndServicesAndInfo: PropTypes.func.isRequired,
  setWixSiteProperties: PropTypes.func.isRequired,
};

export default ClientInformation;
